import React from "react";
import NextLink from "@insightfulscience/smart-react/NextLink";
import Link from "@insightfulscience/atomic-react/Link";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import PageTitle from "@insightfulscience/smart-react/PageTitle";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import PricingSection from "@insightfulscience/atomic-react/r1/molecules/PricingSection";
import CardPrice from "@insightfulscience/atomic-react/CardPrice";
import CardPriceHead from "@insightfulscience/atomic-react/CardPrice/Head";
import CardPriceTitle from "@insightfulscience/atomic-react/CardPrice/Title";
import CardPriceTitleV2 from "@insightfulscience/atomic-react/CardPrice/TitleV2";
import CardPriceBody from "@insightfulscience/atomic-react/CardPrice/Body";
import CardPriceOne from "@insightfulscience/atomic-react/CardPrice/One";
import CardPriceList, { CardPriceListItem } from "@insightfulscience/atomic-react/CardPrice/List";
import CardPriceDescription from "@insightfulscience/atomic-react/CardPrice/Description";
import CardPriceAction from "@insightfulscience/atomic-react/CardPrice/Action";
import CardPriceButtonLink from "@insightfulscience/atomic-react/CardPrice/ButtonLink";
import Button from "@insightfulscience/atomic-react/Button";
import Heading from "@insightfulscience/atomic-react/Heading";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Section from "@insightfulscience/atomic-react/Section";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import { currency } from "@insightfulscience/smart-react/i18-formats";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Line from "@insightfulscience/atomic-react/Line";
import LandingSimpleSection from "@insightfulscience/atomic-react/r2/LandingElement/SimpleSection";
import Html from "@insightfulscience/smart-react/Html";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import Block from "@insightfulscience/atomic-react/Block";
import WithServices from "@src/services";
import SiteLayout from "../src/SiteLayout";
import resources from "../locales/pricing-page";

const HowToBuyPage = () => {
	const [t] = useLocale("how-to-buy");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<PageTitle title={t("PAGE_TITLE")} />
			<Hero>
				<HeroTitle>{t("TITLE")}</HeroTitle>
			</Hero>
			<PricingSection>
				<PricingSection.Header title={t("SUBSCRIPTION_PLANS")}>
					{t("SUBSCRIPTION_PLANS_DESC")} <br />
					{t("SUBSCRIPTION_PLANS_DESC2")}
				</PricingSection.Header>
				<PricingSection.Body>
					<PricingSection.Body.Item>
						<CardPrice className="mb-10">
							<CardPriceHead>
								<CardPriceTitle>{t("ACADEMIC")}</CardPriceTitle>
							</CardPriceHead>
							<CardPriceBody>
								<CardPriceList colOne={t("SEATS")} colTwo={t("PER_YEAR")}>
									<CardPriceListItem
										amount={1}
										price={currency(295, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={2}
										price={currency(500, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={5}
										price={currency(1000, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={10}
										price={currency(1500, { minimumFractionDigits: 0 })}
									/>
								</CardPriceList>
								<CardPriceDescription>{t("ACADEMIC_DESC")}</CardPriceDescription>
								<CardPriceAction>
									<NextLink
										Component={CardPriceButtonLink}
										data-qa-selector="select-seats-academic-yearly-btn"
										href="/purchase/select-seats?planCode=sg_academic_yearly"
									>
										{t("BUY")}
									</NextLink>
								</CardPriceAction>
							</CardPriceBody>
						</CardPrice>
					</PricingSection.Body.Item>
					<PricingSection.Body.Item>
						<CardPrice className="mb-10">
							<CardPriceHead>
								<CardPriceTitle>{t("CORPORATE")}</CardPriceTitle>
							</CardPriceHead>
							<CardPriceBody>
								<CardPriceList colOne={t("SEATS")} colTwo={t("PER_YEAR")}>
									<CardPriceListItem
										amount={1}
										price={currency(1645, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={2}
										price={currency(3190, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={5}
										price={currency(7975, { minimumFractionDigits: 0 })}
									/>
									<CardPriceListItem
										amount={10}
										price={currency(15450, { minimumFractionDigits: 0 })}
									/>
								</CardPriceList>
								<CardPriceDescription>{t("CORPORATE_DESC")}</CardPriceDescription>
								<CardPriceAction>
									<NextLink
										Component={CardPriceButtonLink}
										data-qa-selector="select-seats-corporate-yearly-btn"
										href="/purchase/select-seats?planCode=sg_corporate_yearly"
									>
										{t("BUY")}
									</NextLink>
								</CardPriceAction>
							</CardPriceBody>
						</CardPrice>
					</PricingSection.Body.Item>
				</PricingSection.Body>
				<PricingSection.Footer>
					<PricingSection.Footer.Title>{t("SUBSCRIPTION_INCLUDES")}</PricingSection.Footer.Title>
					<PricingSection.Footer.List>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM1")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM2")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM3")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM4")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM5")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM6")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("SUBSCRIPTION_INCLUDE_ITEM7")}
						</PricingSection.Footer.List.Item>
					</PricingSection.Footer.List>
					<PricingSection.Footer.Description>
						{t("SUBSCRIPTION_PLANS_FOOTER")}{" "}
						<Link sx={{ fontSize: "s" }} href="mailto:sales@snapgene.com">
							sales@snapgene.com
						</Link>
						.
					</PricingSection.Footer.Description>
				</PricingSection.Footer>
			</PricingSection>
			<PricingSection>
				<PricingSection.Header title={t("STUDENT_SUBSCRIPTION")}>
					{t("STUDENT_SUBSCRIPTION_DESC")}
				</PricingSection.Header>
				<PricingSection.Body>
					<PricingSection.Body.Item center>
						<CardPrice className="mb-10">
							<CardPriceHead>
								<CardPriceTitleV2>{t("ANNUAL_STUDENT")}</CardPriceTitleV2>
							</CardPriceHead>
							<CardPriceBody>
								<CardPriceOne
									price={currency(149, { minimumFractionDigits: 0 })}
									period={t("PER_YEAR")}
								/>
								<CardPriceAction>
									<NextLink
										Component={CardPriceButtonLink}
										href="/purchase/select-seats?planCode=sg_student_yearly"
									>
										{t("BUY")}
									</NextLink>
								</CardPriceAction>
							</CardPriceBody>
						</CardPrice>
					</PricingSection.Body.Item>
				</PricingSection.Body>
				<PricingSection.Footer>
					<PricingSection.Footer.Title>{t("AVAILABLE_FOR")}</PricingSection.Footer.Title>
					<PricingSection.Footer.List>
						<PricingSection.Footer.List.Item>
							{t("AVAILABLE_FOR_ITEM1")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("AVAILABLE_FOR_ITEM2")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("AVAILABLE_FOR_ITEM3")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("AVAILABLE_FOR_ITEM4")}
						</PricingSection.Footer.List.Item>
					</PricingSection.Footer.List>
					<PricingSection.Footer.Description>
						{t("STUDENT_SUBSCRIPTION_FOOTER")}
					</PricingSection.Footer.Description>
				</PricingSection.Footer>
			</PricingSection>
			<PricingSection>
				<PricingSection.Header title={t("PERMANENT_LICENSES")}>
					{t("PERMANENT_LICENSES_DESC")}
				</PricingSection.Header>
				<PricingSection.Body>
					<PricingSection.Body.Item center>
						<CardPrice className="mb-10">
							<CardPriceHead>
								<CardPriceTitleV2>{t("PERMANENT_ACADEMIC")}</CardPriceTitleV2>
							</CardPriceHead>
							<CardPriceBody>
								<CardPriceOne
									price={currency(2000, { minimumFractionDigits: 0 })}
									period={t("PER_LICENSE")}
								/>
								<CardPriceAction>
									<NextLink
										Component={CardPriceButtonLink}
										href="https://support.snapgene.com/hc/en-us/requests/new"
									>
										{t("CONTACT")}
									</NextLink>
								</CardPriceAction>
							</CardPriceBody>
						</CardPrice>
					</PricingSection.Body.Item>
					<PricingSection.Body.Item center>
						<CardPrice className="mb-10">
							<CardPriceHead>
								<CardPriceTitleV2>{t("PERMANENT_CORPORATE")}</CardPriceTitleV2>
							</CardPriceHead>
							<CardPriceBody>
								<CardPriceOne
									price={currency(8200, { minimumFractionDigits: 0 })}
									period={t("PER_LICENSE")}
								/>
								<CardPriceAction>
									<NextLink
										Component={CardPriceButtonLink}
										href="https://support.snapgene.com/hc/en-us/requests/new"
									>
										{t("CONTACT")}
									</NextLink>
								</CardPriceAction>
							</CardPriceBody>
						</CardPrice>
					</PricingSection.Body.Item>
				</PricingSection.Body>
				<PricingSection.Footer>
					<PricingSection.Footer.Title>{t("PERMANENT_FOOTER_TITLE")}</PricingSection.Footer.Title>
					<PricingSection.Footer.List>
						<PricingSection.Footer.List.Item>
							{t("PERMANENT_FOOTER_ITEM_1")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("PERMANENT_FOOTER_ITEM_2")}
						</PricingSection.Footer.List.Item>
						<PricingSection.Footer.List.Item>
							{t("PERMANENT_FOOTER_ITEM_3")}
						</PricingSection.Footer.List.Item>
					</PricingSection.Footer.List>
				</PricingSection.Footer>
			</PricingSection>
			<Section className="section">
				<ContentContainer mx px>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }} spacing={{ mb: 6 }}>
						{t("COURSE_LICENSES")}
					</Heading>
					<Paragraph sx={{ fontSize: "l", textAlign: "center" }} spacing={{ mb: 6 }}>
						{t("COURSE_LICENSES_DESC1")}
						<br />
						{t("COURSE_LICENSES_DESC2")}
					</Paragraph>
					<Block sx={{ textAlign: "center" }}>
						<Button.Link
							styleType="secondary"
							isOutline2
							size="s"
							href="/free-course-license"
							className="px-8"
						>
							{t("LEARN_MORE")}
						</Button.Link>
					</Block>
				</ContentContainer>
			</Section>
			<Section>
				<Container>
					<Row>
						<Col>
							<Line />
						</Col>
					</Row>
				</Container>
			</Section>
			<LandingSimpleSection>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 15 }}>
					{t("FAQ_TITLE")}
				</LandingSimpleSection.TitleV2>

				<LandingSimpleSection.Body>
					<Row mx>
						<Col md={4}>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_1_TITLE")}</Block>
								<Block>
									<Html Tag="span">
										{t("FAQ_1_DESCRIPTION", {
											link: "/contact",
										})}
									</Html>
								</Block>
							</Block>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_2_TITLE")}</Block>
								<Block>{t("FAQ_2_DESCRIPTION")}</Block>
							</Block>
						</Col>
						<Col md={4}>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_3_TITLE")}</Block>
								<Block>
									<Html Tag="span">
										{t("FAQ_3_DESCRIPTION", {
											link: "/contact",
										})}
									</Html>
								</Block>
							</Block>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_4_TITLE")}</Block>
								<Block>{t("FAQ_4_DESCRIPTION")}</Block>
							</Block>
						</Col>
						<Col md={4}>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_5_TITLE")}</Block>
								<Block>
									<Html Tag="span">
										{t("FAQ_5_DESCRIPTION", {
											link: "/contact",
										})}
									</Html>
								</Block>
							</Block>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_6_TITLE")}</Block>
								<Block>
									<Html Tag="span">
										{t("FAQ_6_DESCRIPTION", {
											link: "/free-course-license",
										})}
									</Html>
								</Block>
							</Block>
							<Block spacing={{ mb: 4 }}>
								<Block sx={{ fontSize: "l", fontWeight: "bold" }}>{t("FAQ_7_TITLE")}</Block>
								<Block>
									<Html Tag="span">
										{t("FAQ_7_DESCRIPTION", {
											link: "/contact",
										})}
									</Html>
								</Block>
							</Block>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
		</SiteLayout>
	);
};

export default pipe(HowToBuyPage, WithLocale({ resources }), WithServices);
